import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["src", "poster", "preload", "autoplay", "muted", "loop"]
const _hoisted_2 = {
  key: 0,
  class: "e-video-player__center-buttons"
}
const _hoisted_3 = {
  key: 0,
  class: "e-video-player__controls"
}
const _hoisted_4 = { class: "e-video-player__time" }
const _hoisted_5 = { class: "e-video-player__time" }

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Icon from '@/elements/Icon.vue';
import utils from '@/utils';
import useDevice from '@/hooks/useDevice';
import { VideoPlayerProps } from '@/types/elements/video-player';


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoPlayer',
  props: {
    src: {},
    poster: {},
    preload: {},
    autoplay: { type: Boolean, default: true },
    loop: { type: Boolean, default: true },
    controls: { type: Boolean, default: false },
    showBigButton: { type: Boolean },
    asBg: { type: Boolean },
    className: {}
  },
  emits: ['play', 'pause', 'ended'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// ---------- Emits ----------
const emit = __emit;

// ---------- Expose（对应 forwardRef / useImperativeHandle）----------
__expose({ play, pause, updateHovered });

// ---------- Device ----------
const { deviceState, deviceComputes } = useDevice();
const { isMobileOrTablet } = deviceComputes;
const { browser } = deviceState;
// ---------- Refs ----------
const rootEl = ref<HTMLDivElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);

// ---------- State ----------
const hovered = ref(false);
const playing = ref(false);
const loading = ref(true);
const ended = ref(false);
// autoplay 场景初始必须静音，浏览器才放行自动播放；首次用户交互后解除静音（见 onUserInteraction）。
// 注意：不能在模板写死 muted="false"——muted 是布尔属性，属性存在即静音，与取值无关。
const muted = ref(!!props.autoplay);
const duration = ref(0);
const currentTime = ref(0);
const progress = ref(0);
const isFullScreen = ref(false);
const barHovered = ref(false);
const hoverProgress = ref(0);

// ---------- Computed ----------
const resolvedSrc = computed(() => {
  return props.src;
});

const rootClass = computed(() => ['e-video-player', props.className, { 'as-bg': props.asBg }]);

const progressStyle = computed(() => ({ width: `${progress.value}%` }));
const controlThumbStyle = computed(() => ({ left: `${progress.value}%` }));
const hoverPopStyle = computed(() => ({ left: `${hoverProgress.value}%` }));

// ---------- 工具函数 ----------
function formatDuration(seconds: number) {
  return utils.math.formatDuration(seconds);
}

// ---------- 播放控制 ----------
function onPlayClick() {
  videoRef.value?.play();
}

function onPauseClick() {
  videoRef.value?.pause();
}

function onReplay() {
  if (!videoRef.value) return;
  ended.value = false;
  playing.value = true;
  videoRef.value.currentTime = 0;
  videoRef.value.play().catch(() => {});
}

// ---------- 视频事件 ----------
function onVideoMeta(e: Event) {
  duration.value = (e.target as HTMLVideoElement).duration;
}

function onVideoPlay() {
  playing.value = true;
  loading.value = false;
  emit('play');
}

function onVideoPause() {
  playing.value = false;
  emit('pause');
}

function onVideoPlaying() {
  loading.value = false;
}

function onVideoWaiting(e: Event) {
  const el = e.target as HTMLVideoElement;
  if (el.currentTime < el.duration) {
    loading.value = true;
  }
}

function onVideoCanPlay() {
  loading.value = false;
}

function onVideoEnded() {
  playing.value = false;
  ended.value = true;
  loading.value = false;
  progress.value = 100;
  emit('ended');
}

function onVideoTimeUpdate(e: Event) {
  const el = e.target as HTMLVideoElement;
  const p =
    el.currentTime === el.duration
      ? 100
      : Number(((el.currentTime * 100) / el.duration).toFixed(2));
  currentTime.value = el.currentTime;
  ended.value = el.currentTime === el.duration;
  progress.value = p;
}

// ---------- 进度条交互 ----------
function onControlBarClick(e: MouseEvent) {
  if (!videoRef.value || videoRef.value.duration <= 0) return;
  const $bar = e.currentTarget as HTMLDivElement;
  const barRect = $bar.getBoundingClientRect();
  const progressNum = (e.clientX - barRect.left) / $bar.scrollWidth;
  const time = videoRef.value.duration * progressNum;
  if (isNaN(time)) return;
  videoRef.value.currentTime = time;
  currentTime.value = time;
  progress.value = Number((progressNum * 100).toFixed(2));
}

function setBarHoveredProgress(e: MouseEvent) {
  if (!videoRef.value) return;
  const $bar = e.currentTarget as HTMLDivElement;
  const progressNum = (e.clientX - $bar.offsetLeft) / $bar.scrollWidth;
  hoverProgress.value = Number((progressNum * 100).toFixed(2));
}

function onBarMouseEnter(e: MouseEvent) {
  barHovered.value = true;
  setBarHoveredProgress(e);
}

function onBarMouseLeave() {
  barHovered.value = false;
}

function onBarMouseMove(e: MouseEvent) {
  setBarHoveredProgress(e);
}

// ---------- 鼠标悬停 ----------
function onMouseEnter() {
  hovered.value = true;
}

function onMouseLeave() {
  hovered.value = false;
}

// ---------- 静音 ----------
function toggleMuted() {
  if (!videoRef.value) return;
  muted.value = !muted.value;
  videoRef.value.muted = muted.value;
}

// ---------- 全屏 ----------
function toggleFullScreen() {
  if (!rootEl.value) return;
  const triggerEl = isMobileOrTablet || browser === 'safari' ? videoRef.value : rootEl.value;

  const _isFullScreen = !isFullScreen.value;
  if (triggerEl !== videoRef.value) {
    isFullScreen.value = _isFullScreen;
  }
  if (_isFullScreen) {
    utils.dom.goFullScreen(triggerEl!);
  } else {
    utils.dom.exitFullScreen();
  }
}

// ---------- Expose 实现 ----------
function play(seekTo?: number) {
  if (!videoRef.value) return;
  if (seekTo !== undefined && seekTo >= 0) {
    videoRef.value.currentTime = seekTo;
    currentTime.value = seekTo;
  }
  videoRef.value.play();
}

function pause() {
  playing.value = false;
  videoRef.value?.pause();
}

function updateHovered(value: boolean) {
  hovered.value = value;
}

// ---------- 全屏变化监听 ----------
function onFullScreenChange() {
  isFullScreen.value = !!document.fullscreenElement;
}

// ---------- 用户交互后解除静音并有声播放 ----------
// 浏览器 autoplay 策略：unmuted play 必须由用户交互触发。
// 监听首次用户交互，交互后解除静音并 play()，此时浏览器会放行走声音；
// 若有声播放仍被拦截（如 scroll 不构成用户激活），回退为静音播放保证画面不中断。
const hasUserInteracted = ref(false);
const userInteractionEvents = ['click', 'scroll', 'keydown', 'touchstart', 'pointerdown'];
function onUserInteraction() {
  if (hasUserInteracted.value) return;
  hasUserInteracted.value = true;
  const video = videoRef.value;
  if (video && props.autoplay) {
    video.muted = false;
    muted.value = false;
    video.play().catch(() => {
      video.muted = true;
      muted.value = true;
      return video.play().catch(() => {});
    });
  }
  userInteractionEvents.forEach((evt) => {
    document.removeEventListener(evt, onUserInteraction, { passive: true } as EventListenerOptions);
  });
}

onMounted(() => {
  const el = rootEl.value!;
  const videoEl = videoRef.value as any;

  el.addEventListener('fullscreenchange', onFullScreenChange, { passive: true });
  el.addEventListener('fullscreenerror', onFullScreenChange, { passive: true });
  videoEl?.addEventListener('fullscreenchange', onFullScreenChange, { passive: true });
  videoEl?.addEventListener('fullscreenerror', onFullScreenChange, { passive: true });

  // 注册全局用户交互监听，首次交互时触发有声播放
  userInteractionEvents.forEach((evt) => {
    document.addEventListener(evt, onUserInteraction, { passive: true });
  });
});

onUnmounted(() => {
  const el = rootEl.value;
  const videoEl = videoRef.value as any;

  el?.removeEventListener('fullscreenchange', onFullScreenChange);
  el?.removeEventListener('fullscreenerror', onFullScreenChange);
  videoEl?.removeEventListener('fullscreenchange', onFullScreenChange);
  videoEl?.removeEventListener('fullscreenerror', onFullScreenChange);

  // 清理用户交互监听
  userInteractionEvents.forEach((evt) => {
    document.removeEventListener(evt, onUserInteraction, { passive: true } as any);
  });
});

// ---------- autoplay 侦听 ----------
// 注意：浏览器策略要求 autoplay + unmuted 必须由用户交互触发，
// 纯自动播放场景下浏览器会阻止 unmuted 视频。此处不再强制 muted，
// 让浏览器自然处理（会自动阻止 unmuted autoplay，需用户点击播放）。
watch(
  () => props.autoplay,
  (val) => {
    if (videoRef.value && val) {
      videoRef.value.play().catch(() => {});
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_SiteSpinner = _resolveComponent("SiteSpinner")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(rootClass.value),
    ref_key: "rootEl",
    ref: rootEl,
    onMouseenter: onMouseEnter,
    onMouseleave: onMouseLeave
  }, [
    _createElementVNode("video", {
      ref_key: "videoRef",
      ref: videoRef,
      src: resolvedSrc.value,
      poster: __props.poster,
      preload: __props.preload ?? 'metadata',
      autoplay: __props.autoplay ?? true,
      muted: muted.value,
      loop: __props.loop,
      playsinline: "",
      "webkit-playsinline": "true",
      "x-webkit-airplay": "allow",
      "x5-video-player-type": "h5",
      "x5-video-player-fullscreen": "true",
      onLoadedmetadata: onVideoMeta,
      onPlay: onVideoPlay,
      onPause: onVideoPause,
      onPlaying: onVideoPlaying,
      onTimeupdate: onVideoTimeUpdate,
      onWaiting: onVideoWaiting,
      onCanplay: onVideoCanPlay,
      onEnded: onVideoEnded
    }, null, 40, _hoisted_1),
    (__props.showBigButton && (_unref(isMobileOrTablet) || hovered.value))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (loading.value && playing.value)
            ? (_openBlock(), _createBlock(_component_SiteSpinner, { key: 0 }))
            : (!loading.value && !playing.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "e-video-player__center-button",
                  onClick: onPlayClick
                }, [
                  _createVNode(Icon, {
                    name: "play",
                    size: "xs"
                  })
                ]))
              : (!loading.value && playing.value && !_unref(isMobileOrTablet))
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    class: "e-video-player__center-button",
                    onClick: onPauseClick
                  }, [
                    _createVNode(Icon, {
                      name: "pause",
                      size: "xs"
                    })
                  ]))
                : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "fade" }, {
      default: _withCtx(() => [
        (__props.controls)
          ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_3, [
              (ended.value)
                ? (_openBlock(), _createBlock(Icon, {
                    key: 0,
                    class: "hand",
                    name: "replay",
                    onClick: onReplay
                  }))
                : (!playing.value)
                  ? (_openBlock(), _createBlock(Icon, {
                      key: 1,
                      class: "hand",
                      name: "play",
                      onClick: onPlayClick
                    }))
                  : (_openBlock(), _createBlock(Icon, {
                      key: 2,
                      class: "hand",
                      name: "pause",
                      onClick: onPauseClick
                    })),
              _createElementVNode("div", _hoisted_4, _toDisplayString(formatDuration(currentTime.value)), 1),
              _createElementVNode("div", {
                class: "e-video-player__control-bar",
                onClick: onControlBarClick,
                onMouseenter: onBarMouseEnter,
                onMouseleave: onBarMouseLeave,
                onMousemove: onBarMouseMove
              }, [
                _cache[0] || (_cache[0] = _createElementVNode("div", { class: "e-video-player__control-track" }, null, -1)),
                _createElementVNode("div", {
                  class: "e-video-player__control-progress",
                  style: _normalizeStyle(progressStyle.value)
                }, null, 4),
                _createElementVNode("div", {
                  class: "e-video-player__control-thumb",
                  style: _normalizeStyle(controlThumbStyle.value)
                }, null, 4),
                (barHovered.value)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "e-video-player__control-hover-pop",
                      style: _normalizeStyle(hoverPopStyle.value)
                    }, _toDisplayString(formatDuration(duration.value * (hoverProgress.value / 100))), 5))
                  : _createCommentVNode("", true)
              ], 32),
              _createElementVNode("div", _hoisted_5, _toDisplayString(formatDuration(duration.value)), 1),
              _createVNode(Icon, {
                class: "hand",
                name: muted.value ? 'mute' : 'un-muted',
                onClick: toggleMuted
              }, null, 8, ["name"]),
              _createVNode(Icon, {
                class: "hand",
                name: isFullScreen.value ? 'exit-full-screen' : 'full-screen',
                onClick: toggleFullScreen
              }, null, 8, ["name"])
            ], 512)), [
              [_vShow, _unref(isMobileOrTablet) || hovered.value]
            ])
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 34))
}
}

})